import { FC, useCallback, useMemo, useState } from 'react';
import { Eye, EyeOff, Pencil, ShieldCheck, Tag, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { GarantieTunnelListItemDto } from 'api/funnel';
import { Sizes } from 'enums/sizes';
import { funnelsAPI } from 'services/swagger-api';
import { useDialog } from 'hooks/useDialog';
import { useActionMenu } from 'components/navigation/Dropdown/GenericDropdownButton';
import { ConfirmationOverlay } from 'components/feedback/ConfirmationOverlay';
import { useFunnelDetail } from '../FunnelDetailContext';
import { useAsyncResource } from 'back-office/shared/hooks/useAsyncResource';
import { BlockCard, StripedList } from 'back-office/shared/components/BlockCard';
import { RowActions } from 'back-office/shared/components/RowActions';
import { GuaranteeModal } from '../forms/GuaranteeModal';
import { GuaranteeCommissionsModal } from '../forms/GuaranteeCommissionsModal';
import { IS_TUNNEL_GLI } from '../funnelConstants';
import { DASH } from '../funnelFormat';

/**
 * Bloc « Garanties », pleine largeur sous le bloc Détail, comme dans l'ancien écran.
 *
 * Disabling a guarantee (TUN-004) removes it temporarily from subscription and pricing.
 * Deleting it is a soft delete: existing quotes and contracts keep it in their documents,
 * which read the tunnel catalogue as it was at their creation date. The back refuses to
 * delete a required guarantee of an active tunnel; its message is shown as an error toast.
 */
export const GuaranteesBlock: FC = () => {
  const { tunnelId, expressionVariables, reloadFunnel, invalidatePricing } =
    useFunnelDetail();
  const { openBlankDialog } = useDialog();
  const { createMenuItem } = useActionMenu();
  const [guaranteeToDelete, setGuaranteeToDelete] = useState<GarantieTunnelListItemDto | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchGuarantees = useCallback(
    () => funnelsAPI.getFunnelGuarantees({ tunnelId, isTunnelGLI: IS_TUNNEL_GLI }),
    [tunnelId]
  );
  const { data, isLoading, error, reload } = useAsyncResource(fetchGuarantees);

  // Garanties du référentiel déjà rattachées : le sélecteur de la modale les écarte,
  // le back refusant qu'une même garantie soit ajoutée deux fois au tunnel.
  const attachedGuaranteeIds = useMemo(
    () => (data ?? []).map((guarantee) => guarantee.garantieId).filter(Boolean) as string[],
    [data]
  );

  // Ajouter, modifier une garantie ou l'une de ses commissions change le prix : le
  // bloc Tarification doit refaire son calcul, pas seulement la liste se rafraîchir.
  const refresh = useCallback(async () => {
    await Promise.all([reload(), reloadFunnel()]);
    invalidatePricing();
  }, [reload, reloadFunnel, invalidatePricing]);

  const toggleActive = useCallback(
    async (guarantee: GarantieTunnelListItemDto) => {
      const garantieTunnelId = guarantee.garantieTunnelId ?? guarantee.id;
      const isActive = guarantee.isActive ?? true;
      try {
        const response = isActive
          ? await funnelsAPI.disableGuaranteeTunnel({ garantieTunnelId })
          : await funnelsAPI.enableGuaranteeTunnel({ garantieTunnelId });
        if (response.isSuccess) {
          toast.success(isActive ? 'Garantie désactivée' : 'Garantie réactivée');
          await refresh();
        } else {
          toast.error(response.errorMessage || 'Le changement de statut a échoué');
        }
      } catch (error) {
        console.error('Changement de statut de la garantie impossible', error);
        toast.error('Le changement de statut a échoué');
      }
    },
    [refresh]
  );

  const deleteGuarantee = async () => {
    if (!guaranteeToDelete) return;
    setIsDeleting(true);
    try {
      const response = await funnelsAPI.deleteGuaranteeTunnel({
        garantieTunnelId: guaranteeToDelete.garantieTunnelId ?? guaranteeToDelete.id,
      });
      if (response.isSuccess) {
        toast.success(response.notification?.message || 'Garantie supprimée');
        await refresh();
      } else {
        toast.error(response.errorMessage || 'La suppression a échoué');
      }
    } catch (error) {
      console.error('Suppression de la garantie impossible', error);
      toast.error('La suppression a échoué');
    } finally {
      setIsDeleting(false);
      setGuaranteeToDelete(null);
    }
  };

  const openGuarantee = useCallback(
    (guarantee?: GarantieTunnelListItemDto) =>
      openBlankDialog(
        { size: Sizes['5xl'] },
        <GuaranteeModal
          tunnelId={tunnelId}
          garantieTunnelId={guarantee?.garantieTunnelId ?? guarantee?.id}
          title={guarantee?.designation || 'Ajouter une garantie au tunnel'}
          attachedGuaranteeIds={attachedGuaranteeIds}
          variables={expressionVariables}
          onSuccess={refresh}
        />
      ),
    [openBlankDialog, tunnelId, attachedGuaranteeIds, expressionVariables, refresh]
  );

  const openCommissions = useCallback(
    (guarantee: GarantieTunnelListItemDto) =>
      openBlankDialog(
        { size: Sizes['5xl'] },
        <GuaranteeCommissionsModal
          garantieTunnelId={guarantee.garantieTunnelId ?? guarantee.id}
          guaranteeLabel={guarantee.designation ?? ''}
          variables={expressionVariables}
          onChanged={refresh}
        />
      ),
    [openBlankDialog, expressionVariables, refresh]
  );

  // Les garanties imposées se lisent en premier : ce sont elles qui définissent le socle
  // du contrat, les optionnelles ne faisant que l'enrichir. À statut égal, on conserve
  // l'ordre d'affichage défini sur le tunnel.
  const rows = useMemo(
    () =>
      [...(data ?? [])].sort((a, b) => {
        const byRequirement = Number(b.isRequired ?? false) - Number(a.isRequired ?? false);
        return byRequirement !== 0
          ? byRequirement
          : (a.ordreAffichage ?? 0) - (b.ordreAffichage ?? 0);
      }),
    [data]
  );

  return (
    <>
      <BlockCard
        title="Garanties"
        description="Liste des garanties incluses dans le tunnel"
        icon={<ShieldCheck className="h-4 w-4" />}
        headerExtra={
          <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold text-white">
            {rows.length}
          </span>
        }
        actions={[
          createMenuItem('add', 'Ajouter une garantie', () => openGuarantee(), { icon: 'add' }),
        ]}
        isLoading={isLoading}
        error={error}
        isEmpty={rows.length === 0}
        emptyLabel="Aucune garantie sur ce tunnel"
        flushBody
      >
        <StripedList>
          {rows.map((guarantee) => (
            <li
              key={guarantee.id}
              className={`flex items-center gap-4 px-5 py-2.5 ${
                guarantee.isActive === false ? 'opacity-60' : ''
              }`}
            >
              <RequirementBadge isRequired={guarantee.isRequired} />
              {guarantee.isActive === false && (
                <span
                  title="Retirée du tunnel de souscription et de la tarification ; les contrats existants la conservent"
                  className="flex-shrink-0 rounded-md bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700"
                >
                  Inactive
                </span>
              )}

              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-zinc-800">
                  {guarantee.designation || DASH}
                </p>
                {guarantee.descriptionCourte && (
                  <p className="truncate text-xs text-gray-400">{guarantee.descriptionCourte}</p>
                )}
              </div>

              <CountChip count={guarantee.franchises?.length} label="franchise" />
              <CountChip count={guarantee.plafonds?.length} label="plafond" />
              {guarantee.aDetailsDocuments && (
                <span
                  title="Franchise et/ou plafond renseignés pour les documents"
                  className="flex-shrink-0 rounded-md bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary"
                >
                  Docs
                </span>
              )}

              <RowActions
                actions={[
                  {
                    tone: 'info',
                    label: 'Commissions et tarification',
                    icon: <Tag className="h-4 w-4" />,
                    onClick: () => openCommissions(guarantee),
                  },
                  {
                    tone: 'edit',
                    label: 'Modifier la garantie',
                    icon: <Pencil className="h-4 w-4" />,
                    onClick: () => openGuarantee(guarantee),
                  },
                  guarantee.isActive === false
                    ? {
                        tone: 'info',
                        label: 'Réactiver la garantie',
                        icon: <Eye className="h-4 w-4" />,
                        onClick: () => toggleActive(guarantee),
                      }
                    : {
                        tone: 'danger',
                        label: 'Désactiver la garantie (retirée du tunnel, conservée sur les contrats)',
                        icon: <EyeOff className="h-4 w-4" />,
                        onClick: () => toggleActive(guarantee),
                      },
                  {
                    tone: 'danger',
                    label: 'Supprimer la garantie',
                    icon: <Trash2 className="h-4 w-4" />,
                    onClick: () => setGuaranteeToDelete(guarantee),
                  },
                ]}
              />
            </li>
          ))}
        </StripedList>
      </BlockCard>

      <ConfirmationOverlay
        isOpen={guaranteeToDelete !== null}
        onClose={() => setGuaranteeToDelete(null)}
        onConfirm={deleteGuarantee}
        isLoading={isDeleting}
        variant="danger"
        title="Supprimer cette garantie du tunnel ?"
        description={`« ${guaranteeToDelete?.designation || DASH} » ne sera plus proposée à la souscription ni tarifée. Les devis et contrats existants la conservent.`}
      />
    </>
  );
};

const RequirementBadge: FC<{ isRequired?: boolean }> = ({ isRequired }) => (
  <span
    className={`w-24 flex-shrink-0 rounded-md px-2 py-0.5 text-center text-[11px] font-semibold ${
      isRequired ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-400'
    }`}
  >
    {isRequired ? 'Obligatoire' : 'Optionnelle'}
  </span>
);

/** Compteur discret de franchises / plafonds, masqué quand il n'y en a aucun. */
const CountChip: FC<{ count?: number; label: string }> = ({ count, label }) => {
  if (!count) return null;
  return (
    <span className="hidden flex-shrink-0 rounded bg-gray-50 px-2 py-0.5 text-[11px] text-gray-400 lg:inline">
      {count} {label}
      {count > 1 ? 's' : ''}
    </span>
  );
};
